import { RadioGroup as RadioGroupPrimitive } from 'radix-ui'
import type * as React from 'react'

import { useDocumentDirection } from '../../hooks/use-document-direction'
import { cn } from '../../lib/utils'

/**
 * Radix writes a `dir` attribute on the group and defaults it to "ltr" when no
 * DirectionProvider wraps the app, so an Arabic screen showed its radios on
 * the left. The document's direction is the default here, as in Combobox.
 */
function RadioGroup({
  className,
  dir,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  const documentDirection = useDocumentDirection()

  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      dir={dir ?? documentDirection}
      className={cn('grid gap-2', className)}
      {...props}
    />
  )
}

/**
 * A 16px round outline with a round paper mark on an ink fill when chosen.
 * The radio is the one control that stays round (The One Rhombus Rule: "The
 * radio keeps a round mark"); its circle is what tells it apart from a
 * checkbox at a glance.
 *
 * Radix keeps the radio behaviour: arrow keys move within the group, and the
 * whole set is one tab stop.
 */
function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        'peer inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-input bg-input-background transition-ui',
        'enabled:hover:border-foreground',
        'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand',
        'aria-invalid:border-danger',
        'disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:data-[state=checked]:bg-border',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="flex size-full items-center justify-center"
      >
        <span className="size-1.5 animate-mark-in rounded-full bg-brand-foreground in-disabled:bg-muted-foreground" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
}

export { RadioGroup, RadioGroupItem }
